import { fetchBaseServiceAPI } from '@/api/baseServiceApi'
import { usePrimarySajuStore } from '@/store/modules/primarySaju'
import { DEV_MODE } from '@/config/dev-mode'

/** 기본값 상수 */
const DEFAULT_운세변화 = {
  월운확률: 0,
  일운확률: 0,
  일운이모지: null,
  생활베스트: { 운세: '-', 확률: 0, 문장: '' },
  추천베스트: { 활동: '-', 확률: 0, 문장: '' },
  주의베스트: { 활동: '-', 확률: 0, 문장: '' },
  요약브리핑: '',
  요약가이드: '',
  일운키워드: [],
}

const DEFAULT_오행변화 = {
  상승에너지: '',
  상승문구: '',
  하강에너지: '',
  하강문구: '',
  일간지영향: '',
  범례: '＊ 목(성장·창의력), 화(열정·활력), 토(안정·신뢰), 금(기준·분별), 수(지혜·유연성)',
}

const DEFAULT_기운변화 = {
  오행정보: {
    비율: { 목: 0, 화: 0, 토: 0, 금: 0, 수: 0 },
    오행등락: { 목: '－', 화: '－', 토: '－', 금: '－', 수: '－' },
  },
  오행변화: { ...DEFAULT_오행변화 },
}

const DEFAULT_변화정보 = { 요약: '', 행운: '', 상세: [] }
const DEFAULT_별자리 = { 별자리: '-', 영문: '-', 심볼: '-', 원소: '-', 키워드: [], 융합분석: '' }
const DEFAULT_행운아이템 = { 색상: '-', 색상코드: '#CCCCCC', 숫자: [], 방향: '-', 음식: '-', 이유: '' }
const DEFAULT_오늘의미션 = { 미션: '-', 보상: '-', 이유: '' }

/**
 * 데일리 브리핑 데이터를 안전하게 정규화
 */
function normalizeBriefingData(data) {
  if (!data) return null
  return {
    기준정보: data.기준정보 || { 이름: '-', 생일: '-', 성별: '-', 나이: '-' },
    운세변화: { ...DEFAULT_운세변화, ...data.운세변화 },
    기운변화: {
      오행정보: {
        비율: { ...DEFAULT_기운변화.오행정보.비율, ...data.기운변화?.오행정보?.비율 },
        오행등락: { ...DEFAULT_기운변화.오행정보.오행등락, ...data.기운변화?.오행정보?.오행등락 },
      },
      오행변화:
        data.기운변화?.오행변화 && typeof data.기운변화.오행변화 === 'object'
          ? { ...DEFAULT_오행변화, ...data.기운변화.오행변화 }
          : { ...DEFAULT_오행변화 },
    },
    변화정보: { ...DEFAULT_변화정보, ...data.변화정보 },
    별자리: { ...DEFAULT_별자리, ...data.별자리 },
    행운아이템: { ...DEFAULT_행운아이템, ...data.행운아이템 },
    오늘의미션: { ...DEFAULT_오늘의미션, ...data.오늘의미션 },
    일별점수: Array.isArray(data.일별점수) ? data.일별점수 : [],
    공유카드: data.공유카드 || {},
  }
}

// 모듈 레벨 싱글턴 상태 — 페이지 재방문 시 데이터 유지
const briefingData = ref(null)
const isLoading = ref(false)
const isLoaded = ref(false)
const hasError = ref(false)

// 배너(브리핑 일러스트, 용신 프로모) 캐시 — DailyBriefing 컴포넌트에서 공유
const bannerCache = {
  briefingIllustSrc: '',
  yongsinPromoBanners: [],
  isLoaded: false,
}

export function getDailyBriefingBannerCache() {
  return bannerCache
}

/** 로그아웃/계정 전환 시 브리핑 캐시 일괄 초기화 */
export function resetDailyBriefingCache() {
  briefingData.value = null
  isLoading.value = false
  isLoaded.value = false
  hasError.value = false
  bannerCache.briefingIllustSrc = ''
  bannerCache.yongsinPromoBanners = []
  bannerCache.isLoaded = false
}

export function useDailyBriefing() {
  const primarySajuStore = usePrimarySajuStore()

  const hasPrimarySaju = computed(() => !!primarySajuStore.primarySajuId)

  const fetchBriefing = async () => {
    if (!hasPrimarySaju.value) return
    isLoading.value = true
    hasError.value = false
    try {
      const result = await fetchBaseServiceAPI({
        guestUserId: primarySajuStore.primarySajuId,
        analysisTypeId: 37,
      })
      briefingData.value = normalizeBriefingData(result?.data)
      isLoaded.value = true
    } catch (e) {
      hasError.value = true
      DEV_MODE && console.error('DailyBriefing fetch error:', e)
    } finally {
      isLoading.value = false
    }
  }

  /** 각 섹션별 computed */
  const energyFlowData = computed(() => briefingData.value?.일별점수 || [])
  const todayEnergy = computed(() => briefingData.value?.운세변화 || DEFAULT_운세변화)
  const luckyItems = computed(() => briefingData.value?.행운아이템 || DEFAULT_행운아이템)
  const dailySummary = computed(() => briefingData.value?.운세변화?.요약브리핑 || '')
  const dailyGuide = computed(() => briefingData.value?.운세변화?.요약가이드 || '')
  const bestData = computed(() => briefingData.value?.운세변화?.생활베스트 || {})
  const recommendData = computed(() => briefingData.value?.운세변화?.추천베스트 || {})
  const cautionData = computed(() => briefingData.value?.운세변화?.주의베스트 || {})
  const fiveElements = computed(() => briefingData.value?.기운변화 || DEFAULT_기운변화)
  const todayMission = computed(() => briefingData.value?.오늘의미션 || DEFAULT_오늘의미션)
  const constellation = computed(() => briefingData.value?.별자리 || DEFAULT_별자리)
  const majorChanges = computed(() => briefingData.value?.변화정보 || DEFAULT_변화정보)
  const userInfo = computed(() => briefingData.value?.기준정보 || {})

  // 호출 시점에 이미 데이터가 캐시되어 있는지 (모듈 레벨 상태)
  const isCachedData = isLoaded.value

  return {
    briefingData,
    isLoading,
    isLoaded,
    hasError,
    hasPrimarySaju,
    fetchBriefing,
    isCachedData,
    energyFlowData,
    todayEnergy,
    luckyItems,
    dailySummary,
    dailyGuide,
    bestData,
    recommendData,
    cautionData,
    fiveElements,
    todayMission,
    constellation,
    majorChanges,
    userInfo,
  }
}
